import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, Lightbulb } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

/**
 * HelpModeProvider — global "tutorial mode" toggle.
 *
 * When ON, every page renders extended inline explanations via <SectionHelp>
 * and every field-level <HelpHint> defaults to expanded. Persisted in
 * localStorage so a user who switches it on stays in tutorial mode across
 * sessions.
 *
 * When OFF (default), <HelpHint> still renders its "?" icon with a short
 * tooltip on hover/focus. <SectionHelp> renders nothing.
 */
const HelpCtx = createContext({ helpMode: false, toggleHelp: () => {} });

export function HelpModeProvider({ children }) {
  const [helpMode, setHelpMode] = useState(() => {
    try {
      return localStorage.getItem("elswitch.helpMode") === "1";
    } catch {
      return false;
    }
  });
  const toggleHelp = useCallback(() => {
    setHelpMode((v) => {
      try {
        localStorage.setItem("elswitch.helpMode", v ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !v;
    });
  }, []);
  const value = useMemo(() => ({ helpMode, toggleHelp }), [helpMode, toggleHelp]);
  return (
    <HelpCtx.Provider value={value}>
      <TooltipProvider delayDuration={150}>{children}</TooltipProvider>
    </HelpCtx.Provider>
  );
}

export function useHelpMode() {
  return useContext(HelpCtx);
}

/**
 * Inline "?" icon beside section titles & form labels. Always renders.
 * Hover/focus reveals the short tip. Short/long content is looked up from
 * i18n (so translators can edit without touching code).
 *   <HelpHint i18nKey="help.providers.title" />
 */
export function HelpHint({ i18nKey, text, className = "" }) {
  const { t } = useTranslation();
  const { helpMode } = useHelpMode();
  const short = text ?? t(`${i18nKey}.short`, { defaultValue: "" });
  if (!short) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={short}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-accent transition-colors ${className}`}
          data-testid={`help-hint-${(i18nKey || "").replace(/\./g, "-")}`}
        >
          <HelpCircle className={helpMode ? "h-4 w-4 text-accent" : "h-4 w-4"} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
        {short}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Section-level tutorial block. Only rendered when tutorial mode is ON.
 * Long-form i18n under `${i18nKey}.long`; falls back to `.short` if long
 * hasn't been translated yet.
 *   <SectionHelp i18nKey="help.providers.title" />
 */
export function SectionHelp({ i18nKey, text, className = "" }) {
  const { t } = useTranslation();
  const { helpMode } = useHelpMode();
  if (!helpMode) return null;
  const long =
    text ??
    t(`${i18nKey}.long`, {
      defaultValue: t(`${i18nKey}.short`, { defaultValue: "" }),
    });
  if (!long) return null;
  return (
    <div
      className={`mb-4 rounded-md border border-accent/30 bg-accent/5 px-3 py-2 text-xs leading-relaxed text-foreground/80 ${className}`}
      data-testid={`section-help-${(i18nKey || "").replace(/\./g, "-")}`}
    >
      <div className="flex items-start gap-2">
        <Lightbulb className="h-3.5 w-3.5 mt-0.5 text-accent shrink-0" />
        <div className="whitespace-pre-line">{long}</div>
      </div>
    </div>
  );
}

/**
 * Small floating toggle for the header. Visual state reflects ON/OFF;
 * labels come from i18n so EN/ES both work.
 */
export function HelpModeToggle() {
  const { t } = useTranslation();
  const { helpMode, toggleHelp } = useHelpMode();
  const label = helpMode ? t("help.toggle.on", "Help: ON") : t("help.toggle.off", "Help");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={helpMode ? "default" : "outline"}
          size="sm"
          onClick={toggleHelp}
          className="h-9 gap-1.5 px-2.5"
          data-testid="help-mode-toggle"
          aria-pressed={helpMode}
        >
          <Lightbulb
            className={`h-4 w-4 ${
              helpMode ? "" : "text-muted-foreground"
            }`}
          />
          <span className="text-xs font-medium hidden sm:inline">{label}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs">
        {t(
          "help.toggle.hint",
          "Show inline tutorial explanations on every page — perfect for first-time setup.",
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default {
  HelpModeProvider,
  useHelpMode,
  HelpHint,
  SectionHelp,
  HelpModeToggle,
};
